import React, { useEffect } from 'react';
import { useAppContext } from './contexts/AppContext.tsx';
import { useAuth, useCanEdit } from './contexts/AuthContext.tsx';
import { CanEdit } from './components/PermissionGate.tsx';
import { usePayrollUI, usePayrollLoadingContext, usePayrollSettingsContext } from './contexts/PayrollContext.tsx';
import { logPerformanceCritical } from './utils/payrollLogger.ts';
import { calculateSalaryTotal, calculatePureTargetSales, calculateTeamStatistics } from './utils/payrollCalculations.ts';
import {
  useRenderOptimization,
  useMonthChangeOptimization,
} from './hooks/usePerformanceOptimization.ts';
// Phase 2: Extracted payroll hooks
import { usePayrollBusinessLogic } from './hooks/payroll/usePayrollBusinessLogic.tsx';
// Phase 2: Extracted payroll components
import { PayrollRenderProvider } from './components/payroll/PayrollRenderContext.tsx';
import { PayrollHeader } from './components/payroll/PayrollHeader.tsx';
import { TeamBudgetSection } from './components/payroll/TeamBudgetSection.tsx';
import { EmployeeTableSection } from './components/payroll/EmployeeTableSection.tsx';
import { PayrollSummary } from './components/payroll/PayrollSummary.tsx';
import { shouldMaskDirectorData, isSelfEmployee } from './utils/teamPerformanceUtils.ts';
import { getPayrollEditMode, canEditPayrollField } from './utils/payrollPermissions.ts';

interface PayrollManagementProps {
  selectedMonth: string; // YYYY-MM 형식
}

const PayrollManagement: React.FC<PayrollManagementProps> = ({ selectedMonth }) => {
  // 🚀 렌더링 최적화
  const { resetRenderCount } = useRenderOptimization('PayrollManagement');
  const { isMonthChanged } = useMonthChangeOptimization(selectedMonth);
  
  // 사용자 정보 및 권한 가져오기
  const { user } = useAuth();
  const canEditPayroll = useCanEdit('payroll');

  // 🔒 급여 편집 모드 — full(관리자) / limited(실장: 연장근로시간·개별수당만) / none(읽기 전용)
  //    화면 잠금(EmployeeTableSection)과 저장 범위(saveLimitedPayrollData)가 같은 판정을 쓴다.
  const payrollEditMode = React.useMemo(
    () => getPayrollEditMode(user?.permissions as string[] | undefined),
    [user?.permissions]
  );
  /** 이 급여 필드를 지금 사용자가 편집할 수 있는가 */
  const canEditField = React.useCallback(
    (field: string) => canEditPayrollField(payrollEditMode, field),
    [payrollEditMode]
  );
  
  // 월 변경 시 렌더링 카운터 리셋
  useEffect(() => {
    if (isMonthChanged) {
      resetRenderCount();
      logPerformanceCritical('월 변경 - 렌더링 카운터 리셋', { selectedMonth });
    }
  }, [isMonthChanged, selectedMonth, resetRenderCount]);
  
  const {
    employees: globalEmployees, // 전역 employees 데이터
    teams: teamsData, // AppContext의 teams는 TeamData[]
    budgetData, 
    isLoading, 
    saveTeamData,     // 추가
    setTeams: setTeamsContext, // AppContext의 setTeams (TeamData[] 용)
    setBudgetData,
    lastSaveTime: contextLastSaveTime, // AppContext의 lastSaveTime
    addDeletedEmployeeName, // 삭제된 직원 이름을 추가하는 함수
    setEmployees: setGlobalEmployees, // 전역 직원 마스터 setter (로컬 월별 setEmployees와 구분)
    deletedEmployeeNames, // 전역 삭제 대기 목록
    clearDeletedEmployeeNames // 전역 삭제 대기 목록 초기화 함수
  } = useAppContext();
  
  // 직원 역할 사용자의 소속 팀 확인
  const userTeamName = React.useMemo(() => {
    if (user?.role === 'employee' && user.teamId) {
      const userTeam = teamsData.find(t => t.id === user.teamId);
      return userTeam?.name || null;
    }
    return null;
  }, [user, teamsData]);
  
  // 마스킹 여부 확인 함수
  const shouldMaskTeamData = (teamName: string, position?: string, employeeId?: string | number): boolean => {
    // 실장 데이터는 admin과 '본인'만 볼 수 있음 (본인 판정은 계정↔직원 수동 연결 기준)
    if (position && shouldMaskDirectorData(position, user?.role, isSelfEmployee(user?.employeeId, employeeId))) return true;
    // 기존: employee 역할의 타팀 마스킹
    if (user?.role !== 'employee') return false;
    return userTeamName !== teamName;
  };
  
  // 마스킹된 값 반환 함수

  // 급여 관리 확인 버튼 관련 함수들 (EmployeePerformanceTable에서 가져옴)
  const handlePayrollDataConfirmClick = (dataKey: string) => {
    setPendingPayrollDataKey(dataKey);
    setShowPayrollWarningModal(true);
  };

  const handlePayrollWarningConfirm = () => {
    setShowPayrollWarningModal(false);
    setVisiblePayrollData(prev => new Set([...prev, pendingPayrollDataKey]));
    setPendingPayrollDataKey('');
  };

  const handlePayrollWarningCancel = () => {
    setShowPayrollWarningModal(false);
    setPendingPayrollDataKey('');
  };

  // 급여 데이터 표시 여부 확인 함수
  const isPayrollDataVisible = (employeeId: number | string, dataType: string): boolean => {
    return visiblePayrollData.has(`${employeeId.toString()}-${dataType}`);
  };

  // 급여 확인 버튼 또는 데이터 렌더링 함수
  const renderPayrollDataCell = (
    employeeId: number | string,
    dataType: string,
    value: any,
    teamName: string,
    className: string = "text-gray-500",
    suffix: string = "",
    position?: string
  ) => {
    const dataKey = `${employeeId.toString()}-${dataType}`;

    // 🔗 employeeId를 본인 판정에 넘긴다. 안 넘기면 실장 본인에게도 자기 급여가 **** 로 가려진다.
    if (shouldMaskTeamData(teamName, position, employeeId)) {
      return <span className="text-gray-500">****</span>;
    }

    if (isPayrollDataVisible(employeeId, dataType)) {
      return <span className={className}>{value}{suffix}</span>;
    }

    return (
      <button
        onClick={() => handlePayrollDataConfirmClick(dataKey)}
        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
      >
        확인
      </button>
    );
  };

  // 합계 행용 확인 버튼 또는 데이터 렌더링 함수
  const renderSummaryPayrollDataCell = (
    dataType: string,
    value: any,
    className: string = "text-gray-500",
    suffix: string = ""
  ) => {
    const dataKey = `summary-${dataType}`;
    
    if (visiblePayrollData.has(dataKey)) {
      return <span className={className}>{value}{suffix}</span>;
    }

    return (
      <button
        onClick={() => handlePayrollDataConfirmClick(dataKey)}
        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
      >
        확인
      </button>
    );
  };
  
  // Authentication and permissions

  // === UI State Context (Phase 2 Step 5) ===
  const {
    showComparisonWithPreviousMonth,
    setShowComparisonWithPreviousMonth,
    showPayrollWarningModal,
    setShowPayrollWarningModal,
    pendingPayrollDataKey,
    setPendingPayrollDataKey,
    visiblePayrollData,
    setVisiblePayrollData,
    isComposing,
    setIsComposing,
    tempNameValues,
    setTempNameValues,
  } = usePayrollUI();

  // === Loading State Context (Phase 2 Step 5) ===
  const {
    isLoadingActualSales,
    setIsLoadingActualSales,
    isLoadingPreviousMonthData,
    setIsLoadingPreviousMonthData,
    isSaving,
    setIsSaving,
    isBulkAdjustmentRunning,
    setIsBulkAdjustmentRunning,
    isGlobalCalculationRunning,
    setIsGlobalCalculationRunning,
    executingTeams,
    setExecutingTeams,
  } = usePayrollLoadingContext();

  // === Settings State Context (Phase 2 Step 5) ===
  const {
    underperformanceDeductionRate,
    setUnderperformanceDeductionRate,
    applyOvertimeMultiplier,
    setApplyOvertimeMultiplier,
    applyPreviousMonthAdjustment,
    setApplyPreviousMonthAdjustment,
    adjustmentAppliedTeams,
    setAdjustmentAppliedTeams,
    manualToggleTimestamp,
    setManualToggleTimestamp,
  } = usePayrollSettingsContext();

  // ===== Phase 2: Business Logic Hook (sub-hooks called internally) =====
  const {
    employees, setEmployees,
    previousMonthEmployees, importedFromMonth,
    teamActualSales, teamIncentiveCalculations,
    previousMonthData, setPreviousMonthData,
    originalTeams, originalTargetSales, setOriginalTargetSales,
    fieldChangeStatus, pendingTeamRemoval,
    fieldSaveStatus, savingFields,
    localTeams, setLocalTeams,
    isSavingRef, isCalculatingIncentiveRef, isAddingEmployeeRef, isManualRecalculatingRef,
    markFieldAsChanged, markFieldAsSaved, getFieldStyle, SaveStatusIcon,
    totalIncentive, monthlyTeamTargetSales, totalTargetSales, teamStatsData,
    isDataChanged, setIsDataChanged,
    lastSaveTime, changeLog, setChangeLog,
    teamSortType, setTeamSortType,
    ensureUnassignedTeamExists, getSortedTeams, addChangeLog,
    addTeam, removeTeam,
    updateEmployeeTeam, updateNonTaxable, updateEmployeeSalary,
    updateEmployeeFamilyCount, updateEmployeePosition,
    updateDirectorIncentiveRate, updateDirectorTeamAllocation,
    updatePersonnelPercentage, getOriginalValue,
    updateAllowance, updateOvertimeMinutes, updateEmployeeName,
    checkedItems, setCheckedItems, allChecked, setAllChecked,
    toggleAllChecked, toggleChecked,
    addEmployee, deleteEmployees, deleteEmployeesGlobally,
    validateDataBeforeSave, internalSaveAllData, saveSpecificMonthData, saveLimitedPayrollData,
    updateTeamIncentiveRatio, updateUnderperformanceDeductionRate, renderMonthlyComparison,
    // From usePayrollCalculations (called inside business logic hook)
    calculateDeductions, calculateNetSalaryWithAllowances, calculateRealTimeIndividualIncentive,
    getEffectivePersonnelPercentage,
    // From useMonthlyData (called inside business logic hook)
    handleImportPreviousMonth, handleAdjustmentToggle, lastLoadedMonthRef,
  } = usePayrollBusinessLogic({
    selectedMonth,
    user, canEditPayroll,
    budgetData, setBudgetData,
    globalEmployees, setGlobalEmployees, teamsData, setTeamsContext,
    saveTeamData,
    addDeletedEmployeeName, deletedEmployeeNames, clearDeletedEmployeeNames, isLoading,
  });

  if (isLoading) { 
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-700">데이터를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  // 탭 기능 제거


  // ===== Phase 2: PayrollRenderContext value =====
  const payrollRenderContext = {
    // Auth & permissions
    CanEdit,
    canEditPayroll,
    user,
    userTeamName,
    shouldMaskTeamData,
    // Core data
    budgetData,
    employees,
    globalEmployees,
    localTeams,
    teamsData,
    selectedMonth,
    // UI state
    changeLog,
    isDataChanged,
    isSaving,
    lastSaveTime,
    contextLastSaveTime,
    checkedItems,
    allChecked,
    isComposing,
    tempNameValues,
    applyOvertimeMultiplier,
    showComparisonWithPreviousMonth,
    showPayrollWarningModal,
    teamSortType,
    underperformanceDeductionRate,
    monthlyTeamTargetSales,
    totalTargetSales,
    importedFromMonth,
    previousMonthData,
    previousMonthEmployees,
    applyPreviousMonthAdjustment,
    isLoadingPreviousMonthData,
    isLoadingActualSales,
    teamActualSales,
    totalIncentive,
    SaveStatusIcon,
    // Setters
    setBudgetData,
    setEmployees,
    setIsDataChanged,
    setIsComposing,
    setTempNameValues,
    setApplyOvertimeMultiplier,
    setShowComparisonWithPreviousMonth,
    setTeamSortType,
    setIsLoadingActualSales,
    teamIncentiveCalculations,
    // Functions (from utils)
    calculateSalaryTotal,
    calculatePureTargetSales,
    calculateTeamStatistics,
    // Functions (from hooks)
    calculateDeductions,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    getEffectivePersonnelPercentage,
    getSortedTeams,
    handleAdjustmentToggle,
    handleImportPreviousMonth,
    // Functions (local)
    addEmployee,
    addTeam,
    deleteEmployees,
    deleteEmployeesGlobally,
    getFieldStyle,
    handlePayrollWarningCancel,
    handlePayrollWarningConfirm,
    internalSaveAllData,
    saveLimitedPayrollData,
    payrollEditMode,
    canEditField,
    removeTeam,
    renderMonthlyComparison,
    renderPayrollDataCell,
    renderSummaryPayrollDataCell,
    toggleAllChecked,
    toggleChecked,
    updateAllowance,
    updateEmployeeFamilyCount,
    updateEmployeeName,
    updateEmployeePosition,
    updateDirectorIncentiveRate,
    updateDirectorTeamAllocation,
    updateEmployeeSalary,
    updateEmployeeTeam,
    updateNonTaxable,
    updateOvertimeMinutes,
    updatePersonnelPercentage,
    updateTeamIncentiveRatio,
    updateUnderperformanceDeductionRate,
  };

  return (
    <PayrollRenderProvider value={payrollRenderContext}>
      <div className="flex flex-col p-3 sm:p-4 md:p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen font-sans">
        {/* 탭 네비게이션 제거됨 */}
        <PayrollHeader />
        <TeamBudgetSection />
        <EmployeeTableSection />
        <PayrollSummary />
      </div>
    </PayrollRenderProvider>
  );
};

export default PayrollManagement;

